import { useState, useEffect, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import {
  Phone, CheckCircle2, Clock, Users, TrendingUp,
  LogOut, Trash2, Check, MessageSquare, Calendar,
  ChevronDown, ChevronUp, Loader2, AlertCircle, Eye,
  Upload, FileText, Settings, Wallet,
} from "lucide-react";
import { toast } from "sonner";
import {
  dashboardProLogin, dashboardProCalls,
  dashboardProMarkDone, dashboardProDeleteCall,
  dashboardProStats, dashboardProCallDetail, dashboardProWallet,
  dashboardProGetSettings, dashboardProUpdateSettings,
  dashboardProListKnowledge, dashboardProUploadKnowledge, dashboardProDeleteKnowledge,
} from "../lib/api";

// saldo → colore: rosso <0, giallo <5, verde altrimenti
const balColor = (b) => (b < 0 ? "#C0392B" : b < 5 ? "#D9A441" : "#3A4D39");
const fmtEur = (n) => `€${(Math.round((Number(n) || 0) * 100) / 100).toFixed(2)}`;

const TOKEN_KEY = (slug) => `dp_token_${slug}`;
const COMPANY_KEY = (slug) => `dp_company_${slug}`;

export default function DashboardPro() {
  const { slug } = useParams();
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY(slug)));
  const [companyName, setCompanyName] = useState(() => localStorage.getItem(COMPANY_KEY(slug)) || "");

  const handleLogin = (tok, name) => {
    localStorage.setItem(TOKEN_KEY(slug), tok);
    localStorage.setItem(COMPANY_KEY(slug), name);
    setToken(tok);
    setCompanyName(name);
  };

  const handleLogout = () => {
    localStorage.removeItem(TOKEN_KEY(slug));
    localStorage.removeItem(COMPANY_KEY(slug));
    setToken(null);
    setCompanyName("");
  };

  if (!token) return <LoginView slug={slug} onLogin={handleLogin} />;
  return <MainDashboard slug={slug} token={token} companyName={companyName} onLogout={handleLogout} />;
}

function LoginView({ slug, onLogin }) {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!password.trim()) return;
    setLoading(true);
    try {
      const res = await dashboardProLogin(slug, password);
      onLogin(res.token, res.company_name);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Password errata");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-brand-bg flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <p className="text-xs tracking-[0.3em] text-brand-terra uppercase mb-2">24Vox</p>
          <h1 className="font-heading text-3xl text-brand-ink">Dashboard</h1>
          <p className="text-sm text-brand-inkSoft mt-2">Riservata ai clienti 24Vox</p>
        </div>
        <form onSubmit={handleSubmit} className="bg-white border border-brand-line rounded-2xl p-8 shadow-sm space-y-4">
          <div>
            <label className="text-xs uppercase tracking-wider text-brand-inkSoft block mb-2">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="input-clean w-full px-4 py-3 rounded-lg text-base"
              autoComplete="current-password"
              autoFocus
              required
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="btn-olive w-full py-3 rounded-lg font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Phone className="w-5 h-5" />}
            Accedi
          </button>
        </form>
        <p className="text-center text-xs text-brand-inkSoft mt-4">
          Dashboard per: <span className="text-brand-ink font-medium">{slug}</span>
        </p>
      </div>
    </div>
  );
}

function MainDashboard({ slug, token, companyName, onLogout }) {
  const [activeTab, setActiveTab] = useState("azioni");
  const [calls, setCalls] = useState([]);
  const [stats, setStats] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [callsRes, statsRes, walletRes] = await Promise.all([
        dashboardProCalls(slug, token),
        dashboardProStats(slug, token),
        dashboardProWallet(slug, token).catch(() => null),
      ]);
      setCalls(callsRes.calls || []);
      setStats(statsRes);
      setWallet(walletRes);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Sessione scaduta — accedi di nuovo");
        onLogout();
      } else {
        toast.error("Errore nel caricare i dati");
      }
    } finally {
      setLoading(false);
    }
  }, [slug, token, onLogout]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const handleMarkDone = async (callId) => {
    try {
      await dashboardProMarkDone(slug, callId, token);
      setCalls((prev) => prev.map((c) => c.call_id === callId ? { ...c, azione_done: true } : c));
      if (stats) setStats((s) => ({ ...s, pending_actions: Math.max(0, (s.pending_actions || 1) - 1), done_actions: (s.done_actions || 0) + 1 }));
      toast.success("Azione completata ✓");
    } catch { toast.error("Errore nel salvare"); }
  };

  const handleDelete = async (callId) => {
    if (!window.confirm("Eliminare questa chiamata?")) return;
    try {
      await dashboardProDeleteCall(slug, callId, token);
      setCalls((prev) => prev.filter((c) => c.call_id !== callId));
      if (stats) setStats((s) => ({ ...s, total: Math.max(0, (s.total || 1) - 1) }));
      toast.success("Chiamata eliminata");
    } catch { toast.error("Errore nell'eliminazione"); }
  };

  const pendingCount = calls.filter((c) => !c.azione_done && c.summary?.azione_richiesta).length;

  const TABS = [
    { id: "azioni", label: "Azioni", badge: pendingCount },
    { id: "storico", label: "Storico", badge: null },
    { id: "clienti", label: "Clienti", badge: null },
    { id: "statistiche", label: "Statistiche", badge: null },
    { id: "impostazioni", label: "Impostazioni", badge: null },
  ];

  return (
    <div className="min-h-screen bg-brand-bg">
      <header className="sticky top-0 z-20 bg-white/95 backdrop-blur border-b border-brand-line">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <div>
            <p className="text-[10px] tracking-[0.2em] text-brand-terra uppercase">24Vox · Dashboard</p>
            <p className="font-heading text-base text-brand-ink leading-tight">{companyName || slug}</p>
          </div>
          <div className="flex items-center gap-2">
            {wallet && (
              <div className="flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg bg-brand-bg border border-brand-line" title="Credito residuo">
                <Wallet className="w-4 h-4 text-brand-inkSoft" />
                <span className="font-medium" style={{ color: balColor(wallet.balance_eur) }}>{fmtEur(wallet.balance_eur)}</span>
              </div>
            )}
            <button onClick={onLogout} className="flex items-center gap-1.5 text-xs text-brand-inkSoft hover:text-brand-ink transition px-3 py-2 rounded-lg hover:bg-brand-bg">
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">Esci</span>
            </button>
          </div>
        </div>
        <div className="max-w-5xl mx-auto px-4 flex gap-0 overflow-x-auto">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`relative flex-shrink-0 px-4 py-2.5 text-sm font-medium transition-all border-b-2 ${
                activeTab === tab.id
                  ? "border-brand-olive text-brand-olive"
                  : "border-transparent text-brand-inkSoft hover:text-brand-ink"
              }`}
            >
              {tab.label}
              {tab.badge > 0 && (
                <span className="ml-1.5 bg-brand-terra text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full leading-none">
                  {tab.badge}
                </span>
              )}
            </button>
          ))}
        </div>
      </header>

      {stats && (
        <div className="max-w-5xl mx-auto px-4 py-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
          <StatCard label="Chiamate totali" value={stats.total} icon={<Phone className="w-4 h-4" />} color="text-brand-ink" />
          <StatCard label="Azioni pendenti" value={stats.pending_actions} icon={<Clock className="w-4 h-4" />} color="text-brand-terra" />
          <StatCard label="Clienti unici" value={stats.unique_clients} icon={<Users className="w-4 h-4" />} color="text-brand-olive" />
          <StatCard label="Questa settimana" value={stats.this_week} icon={<TrendingUp className="w-4 h-4" />} color="text-brand-ink" />
        </div>
      )}

      <main className="max-w-5xl mx-auto px-4 pb-12">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-brand-inkSoft" />
          </div>
        ) : (
          <>
            {activeTab === "azioni" && (
              <AzioniTab calls={calls} onMarkDone={handleMarkDone} onDelete={handleDelete} slug={slug} token={token} />
            )}
            {activeTab === "storico" && (
              <StoricoTab calls={calls} onDelete={handleDelete} slug={slug} token={token} />
            )}
            {activeTab === "clienti" && <ClientiTab calls={calls} />}
            {activeTab === "statistiche" && <StatisticheTab stats={stats} calls={calls} />}
            {activeTab === "impostazioni" && <ImpostazioniTab slug={slug} token={token} />}
          </>
        )}
      </main>
    </div>
  );
}

function StatCard({ label, value, icon, color }) {
  return (
    <div className="bg-white border border-brand-line rounded-xl px-4 py-3">
      <div className={`flex items-center gap-1.5 mb-1`}>
        <span className={color}>{icon}</span>
        <span className="text-xs uppercase tracking-wider text-brand-inkSoft">{label}</span>
      </div>
      <p className={`font-heading text-3xl ${color}`}>{value ?? "—"}</p>
    </div>
  );
}

function AzioniTab({ calls, onMarkDone, onDelete, slug, token }) {
  const pending = calls.filter((c) => !c.azione_done && c.summary?.azione_richiesta);
  const done = calls.filter((c) => c.azione_done && c.summary?.azione_richiesta);

  if (!pending.length && !done.length) {
    return (
      <EmptyState
        icon={<CheckCircle2 className="w-12 h-12 text-brand-line" strokeWidth={1.5} />}
        title="Nessuna azione pendente"
        subtitle="Quando un cliente chiama, qui apparirà cosa fare dopo la chiamata."
      />
    );
  }

  return (
    <div className="space-y-6">
      {pending.length > 0 && (
        <section>
          <h2 className="text-xs uppercase tracking-widest text-brand-terra mb-3 font-semibold flex items-center gap-2">
            <Clock className="w-3.5 h-3.5" /> Da fare ({pending.length})
          </h2>
          <div className="space-y-3">
            {pending.map((c) => (
              <AzioneCard key={c.call_id} call={c} onMarkDone={onMarkDone} onDelete={onDelete} slug={slug} token={token} />
            ))}
          </div>
        </section>
      )}
      {done.length > 0 && (
        <section>
          <h2 className="text-xs uppercase tracking-widest text-brand-inkSoft mb-3 font-semibold flex items-center gap-2">
            <CheckCircle2 className="w-3.5 h-3.5" /> Completate ({done.length})
          </h2>
          <div className="space-y-3 opacity-60">
            {done.map((c) => (
              <AzioneCard key={c.call_id} call={c} onMarkDone={onMarkDone} onDelete={onDelete} slug={slug} token={token} isDone />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function AzioneCard({ call, onMarkDone, onDelete, isDone, slug, token }) {
  const s = call.summary || {};
  const [showDetail, setShowDetail] = useState(false);

  return (
    <div className={`bg-white border rounded-xl overflow-hidden ${isDone ? "border-brand-line" : "border-brand-olive/40 shadow-sm"}`}>
      <div className="p-4">
        {s.azione_richiesta && (
          <div className={`flex items-start gap-2 mb-3 px-3 py-2 rounded-lg ${isDone ? "bg-brand-bg" : "bg-amber-50"}`}>
            {isDone
              ? <CheckCircle2 className="w-4 h-4 text-brand-olive mt-0.5 shrink-0" />
              : <AlertCircle className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
            }
            <p className={`text-sm font-semibold ${isDone ? "text-brand-inkSoft line-through" : "text-brand-ink"}`}>
              {s.azione_richiesta}
            </p>
          </div>
        )}
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-brand-inkSoft mb-3">
          {s.nome_cliente && (
            <span className="flex items-center gap-1">
              <Users className="w-3 h-3" />{s.nome_cliente}
            </span>
          )}
          {s.telefono_cliente && (
            <a href={`tel:+${s.telefono_cliente}`} className="flex items-center gap-1 text-brand-olive font-medium hover:underline">
              <Phone className="w-3 h-3" />+{s.telefono_cliente}
            </a>
          )}
          {s.motivo_chiamata && (
            <span className="flex items-center gap-1">
              <MessageSquare className="w-3 h-3" />{s.motivo_chiamata}
            </span>
          )}
          <span className="flex items-center gap-1">
            <Calendar className="w-3 h-3" />{fmtDate(call.created_at)}
          </span>
        </div>
        {s.riassunto_breve && (
          <p className="text-sm text-brand-inkSoft italic mb-3 border-l-2 border-brand-line pl-3">{s.riassunto_breve}</p>
        )}
        <div className="flex items-center gap-2">
          {!isDone && (
            <button
              onClick={() => onMarkDone(call.call_id)}
              className="flex items-center gap-1.5 text-sm bg-brand-olive text-white px-3 py-1.5 rounded-lg font-medium hover:opacity-90 transition"
            >
              <Check className="w-4 h-4" /> Fatto
            </button>
          )}
          <button
            onClick={() => setShowDetail(!showDetail)}
            className="flex items-center gap-1.5 text-xs text-brand-inkSoft hover:text-brand-ink transition px-2 py-1.5"
          >
            <Eye className="w-3.5 h-3.5" />{showDetail ? "Chiudi" : "Dettagli"}
          </button>
          <button
            onClick={() => onDelete(call.call_id)}
            className="ml-auto flex items-center gap-1 text-xs text-brand-inkSoft hover:text-red-500 transition px-2 py-1.5"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
      {showDetail && <CallDetail call={call} slug={slug} token={token} />}
    </div>
  );
}

function StoricoTab({ calls, onDelete, slug, token }) {
  if (!calls.length) {
    return (
      <EmptyState
        icon={<Phone className="w-12 h-12 text-brand-line" strokeWidth={1.5} />}
        title="Nessuna chiamata ancora"
        subtitle="Le chiamate appariranno qui dopo la prima conversazione."
      />
    );
  }
  return (
    <div className="space-y-2">
      {calls.map((c) => (
        <StoricoCard key={c.call_id} call={c} onDelete={onDelete} slug={slug} token={token} />
      ))}
    </div>
  );
}

function StoricoCard({ call, onDelete, slug, token }) {
  const [expanded, setExpanded] = useState(false);
  const s = call.summary || {};
  return (
    <div className="bg-white border border-brand-line rounded-xl overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full p-4 flex items-center justify-between hover:bg-brand-bg transition text-left"
      >
        <div className="flex items-center gap-3">
          <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${
            call.azione_done ? "bg-brand-olive/10" : s.azione_richiesta ? "bg-amber-50" : "bg-brand-bg"
          }`}>
            <Phone className={`w-4 h-4 ${
              call.azione_done ? "text-brand-olive" : s.azione_richiesta ? "text-amber-500" : "text-brand-inkSoft"
            }`} />
          </div>
          <div className="min-w-0">
            <p className="font-medium text-brand-ink text-sm">{s.nome_cliente || "Cliente sconosciuto"}</p>
            <p className="text-xs text-brand-inkSoft truncate max-w-[220px] sm:max-w-none">
              {s.motivo_chiamata || "—"} · {fmtDate(call.created_at)}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0 ml-2">
          {call.azione_done && (
            <span className="text-[10px] bg-brand-olive/10 text-brand-olive px-2 py-0.5 rounded-full">completata</span>
          )}
          {!call.azione_done && s.azione_richiesta && (
            <span className="text-[10px] bg-amber-50 text-amber-600 px-2 py-0.5 rounded-full">in attesa</span>
          )}
          {expanded ? <ChevronUp className="w-4 h-4 text-brand-inkSoft" /> : <ChevronDown className="w-4 h-4 text-brand-inkSoft" />}
        </div>
      </button>
      {expanded && <CallDetail call={call} slug={slug} token={token} onDelete={onDelete} />}
    </div>
  );
}

function CallDetail({ call, slug, token, onDelete }) {
  const s = call.summary || {};
  const [transcript, setTranscript] = useState(null);
  const [loadingT, setLoadingT] = useState(false);

  const loadTranscript = async () => {
    if (transcript !== null) { setTranscript(null); return; }
    setLoadingT(true);
    try {
      const detail = await dashboardProCallDetail(slug, call.call_id, token);
      setTranscript(detail.messages || []);
    } catch { toast.error("Errore nel caricare la trascrizione"); }
    finally { setLoadingT(false); }
  };

  return (
    <div className="border-t border-brand-line p-4 bg-brand-bg space-y-3">
      {s.riassunto_breve && (
        <div className="bg-white rounded-lg px-4 py-3 italic text-sm text-brand-ink border-l-2 border-brand-olive/30">
          "{s.riassunto_breve}"
        </div>
      )}
      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
        {s.nome_cliente && <DetailRow label="Cliente" value={s.nome_cliente} />}
        {s.telefono_cliente && (
          <div>
            <dt className="text-[10px] uppercase tracking-wider text-brand-inkSoft mb-0.5">Telefono</dt>
            <dd>
              <a href={`tel:+${s.telefono_cliente}`} className="text-brand-olive font-medium hover:underline">
                +{s.telefono_cliente}
              </a>
            </dd>
          </div>
        )}
        {s.motivo_chiamata && <DetailRow label="Motivo" value={s.motivo_chiamata} className="sm:col-span-2" />}
        {s.info_chiave && <DetailRow label="Info chiave" value={s.info_chiave} className="sm:col-span-2" />}
        {s.azione_richiesta && <DetailRow label="Cosa fare" value={s.azione_richiesta} className="sm:col-span-2" bold />}
        {s.contatti_lasciati && <DetailRow label="Contatti" value={s.contatti_lasciati} className="sm:col-span-2" />}
        {s.info_richieste_e_risposte && <DetailRow label="Info scambiate" value={s.info_richieste_e_risposte} className="sm:col-span-2" />}
      </dl>

      <button
        onClick={loadTranscript}
        className="flex items-center gap-1.5 text-xs text-brand-inkSoft hover:text-brand-ink transition"
      >
        {loadingT ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <MessageSquare className="w-3.5 h-3.5" />}
        {transcript !== null ? "Nascondi trascrizione" : "Mostra trascrizione completa"}
      </button>

      {transcript && transcript.length > 0 && (
        <div className="bg-white border border-brand-line rounded-lg p-3 space-y-2 max-h-72 overflow-y-auto">
          {transcript.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[85%] px-3 py-2 rounded-xl text-xs ${
                m.role === "user" ? "bg-brand-olive/10 text-brand-ink" : "bg-brand-bg text-brand-ink"
              }`}>
                <span className="text-[9px] uppercase tracking-wider font-semibold text-brand-inkSoft block mb-0.5">
                  {m.role === "user" ? "Cliente" : "AI"}
                </span>
                {m.content}
              </div>
            </div>
          ))}
        </div>
      )}

      {onDelete && (
        <button
          onClick={() => onDelete(call.call_id)}
          className="flex items-center gap-1 text-xs text-red-400 hover:text-red-600 transition"
        >
          <Trash2 className="w-3 h-3" /> Elimina chiamata
        </button>
      )}
    </div>
  );
}

function DetailRow({ label, value, className = "", bold = false }) {
  return (
    <div className={className}>
      <dt className="text-[10px] uppercase tracking-wider text-brand-inkSoft mb-0.5">{label}</dt>
      <dd className={`text-sm text-brand-ink ${bold ? "font-semibold" : ""}`}>{value}</dd>
    </div>
  );
}

function ClientiTab({ calls }) {
  const clientMap = {};
  for (const c of calls) {
    const s = c.summary || {};
    const phone = s.telefono_cliente || "__unknown__";
    if (!clientMap[phone]) {
      clientMap[phone] = { phone: phone === "__unknown__" ? "" : phone, nome: s.nome_cliente || "Cliente sconosciuto", calls: [] };
    }
    if (s.nome_cliente) clientMap[phone].nome = s.nome_cliente;
    clientMap[phone].calls.push(c);
  }
  const clients = Object.values(clientMap).sort((a, b) => b.calls.length - a.calls.length);

  if (!clients.length) {
    return (
      <EmptyState
        icon={<Users className="w-12 h-12 text-brand-line" strokeWidth={1.5} />}
        title="Nessun cliente ancora"
        subtitle="I clienti appariranno qui dopo le prime chiamate."
      />
    );
  }

  return (
    <div className="space-y-2">
      {clients.map((client) => (
        <ClientCard key={client.phone || client.nome} client={client} />
      ))}
    </div>
  );
}

function ClientCard({ client }) {
  const [expanded, setExpanded] = useState(false);
  const lastCall = client.calls[0];
  const lastMotivo = lastCall?.summary?.motivo_chiamata || "—";

  return (
    <div className="bg-white border border-brand-line rounded-xl overflow-hidden">
      <button onClick={() => setExpanded(!expanded)} className="w-full p-4 flex items-center gap-3 hover:bg-brand-bg transition text-left">
        <div className="w-10 h-10 rounded-full bg-brand-olive/10 flex items-center justify-center shrink-0">
          <span className="font-heading text-brand-olive text-sm">{(client.nome || "?")[0].toUpperCase()}</span>
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm text-brand-ink">{client.nome}</p>
          <div className="flex gap-3 text-xs text-brand-inkSoft mt-0.5">
            {client.phone && (
              <a href={`tel:+${client.phone}`} onClick={(e) => e.stopPropagation()} className="text-brand-olive hover:underline">
                +{client.phone}
              </a>
            )}
            <span>{client.calls.length} {client.calls.length === 1 ? "chiamata" : "chiamate"}</span>
            <span className="truncate max-w-[140px]">{lastMotivo}</span>
          </div>
        </div>
        {expanded ? <ChevronUp className="w-4 h-4 text-brand-inkSoft shrink-0" /> : <ChevronDown className="w-4 h-4 text-brand-inkSoft shrink-0" />}
      </button>
      {expanded && (
        <div className="border-t border-brand-line bg-brand-bg px-4 py-3 space-y-2">
          {client.calls.map((c) => (
            <div key={c.call_id} className="flex items-start gap-2 text-xs">
              <Calendar className="w-3.5 h-3.5 text-brand-inkSoft mt-0.5 shrink-0" />
              <div>
                <span className="text-brand-inkSoft">{fmtDate(c.created_at)}</span>
                {c.summary?.motivo_chiamata && <span className="text-brand-ink ml-1">· {c.summary.motivo_chiamata}</span>}
                {c.summary?.azione_richiesta && (
                  <p className={`mt-0.5 ${c.azione_done ? "text-brand-inkSoft line-through" : "text-brand-terra"}`}>
                    {c.summary.azione_richiesta}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function StatisticheTab({ stats, calls }) {
  if (!stats) return null;

  const total = stats.total || 0;
  const done = stats.done_actions || 0;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;

  const hours = Array(24).fill(0);
  calls.forEach((c) => {
    const h = new Date(c.created_at).getHours();
    if (!isNaN(h)) hours[h]++;
  });
  const peakHour = hours.indexOf(Math.max(...hours));
  const peakCount = hours[peakHour];

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-white border border-brand-line rounded-xl p-4">
          <p className="text-xs uppercase tracking-wider text-brand-inkSoft mb-1">Azioni completate</p>
          <p className="font-heading text-3xl text-brand-olive">
            {done} <span className="text-sm text-brand-inkSoft font-normal">/ {total}</span>
          </p>
          <div className="mt-2 h-2 bg-brand-bg rounded-full overflow-hidden">
            <div className="h-full bg-brand-olive rounded-full transition-all" style={{ width: `${pct}%` }} />
          </div>
          <p className="text-xs text-brand-inkSoft mt-1">{pct}% completate</p>
        </div>
        <div className="bg-white border border-brand-line rounded-xl p-4">
          <p className="text-xs uppercase tracking-wider text-brand-inkSoft mb-1">Ora di punta</p>
          <p className="font-heading text-3xl text-brand-ink">
            {peakCount > 0 ? `${peakHour}:00` : "—"}
          </p>
          <p className="text-xs text-brand-inkSoft mt-1">
            {peakCount > 0 ? `${peakCount} chiamate in quest'ora` : "Nessun dato"}
          </p>
        </div>
      </div>

      {stats.top_motivi?.length > 0 && (
        <div className="bg-white border border-brand-line rounded-xl p-4">
          <h3 className="text-xs uppercase tracking-wider text-brand-inkSoft mb-3 font-semibold">Motivi più frequenti</h3>
          <div className="space-y-3">
            {stats.top_motivi.map((m, i) => {
              const maxCount = stats.top_motivi[0]?.count || 1;
              const barPct = Math.round((m.count / maxCount) * 100);
              return (
                <div key={i}>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-brand-ink truncate max-w-[260px]">{m.motivo}</span>
                    <span className="text-brand-inkSoft ml-2 shrink-0 font-semibold">{m.count}</span>
                  </div>
                  <div className="h-1.5 bg-brand-bg rounded-full overflow-hidden">
                    <div className="h-full bg-brand-olive rounded-full" style={{ width: `${barPct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {stats.daily_calls?.length > 0 && (
        <div className="bg-white border border-brand-line rounded-xl p-4">
          <h3 className="text-xs uppercase tracking-wider text-brand-inkSoft mb-3 font-semibold">Ultimi 14 giorni</h3>
          <div className="flex items-end gap-1 h-24">
            {stats.daily_calls.map((d, i) => {
              const maxD = Math.max(...stats.daily_calls.map((x) => x.count), 1);
              const barH = Math.max(4, Math.round((d.count / maxD) * 80));
              return (
                <div key={i} className="flex-1 flex flex-col items-center gap-1" title={`${d.date}: ${d.count} chiamate`}>
                  <span className="text-[9px] text-brand-inkSoft">{d.count > 0 ? d.count : ""}</span>
                  <div className="w-full bg-brand-olive/80 rounded-t-sm" style={{ height: `${barH}px` }} />
                  <span className="text-[8px] text-brand-inkSoft">{d.date.slice(8)}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {calls.length === 0 && (
        <div className="bg-white border border-brand-line rounded-xl p-8 text-center">
          <p className="text-brand-inkSoft text-sm">Le statistiche appariranno dopo le prime chiamate.</p>
        </div>
      )}
    </div>
  );
}

function ImpostazioniTab({ slug, token }) {
  const [voice, setVoice] = useState("M");
  const [savingVoice, setSavingVoice] = useState(false);
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [knowledge, setKnowledge] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    dashboardProGetSettings(slug, token)
      .then((s) => setVoice(s.default_voice || "M"))
      .catch(() => {})
      .finally(() => setLoadingSettings(false));

    dashboardProListKnowledge(slug, token)
      .then((r) => setKnowledge(r.docs || []))
      .catch(() => {})
      .finally(() => setLoadingDocs(false));
  }, [slug, token]);

  const saveVoice = async () => {
    setSavingVoice(true);
    try {
      await dashboardProUpdateSettings(slug, token, { default_voice: voice });
      toast.success("Voce predefinita salvata");
    } catch { toast.error("Errore nel salvare"); }
    finally { setSavingVoice(false); }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await dashboardProUploadKnowledge(slug, token, file);
      toast.success(`"${res.filename}" caricato — ${res.char_count.toLocaleString()} caratteri`);
      const updated = await dashboardProListKnowledge(slug, token);
      setKnowledge(updated.docs || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore nel caricamento");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const deleteDoc = async (docId, filename) => {
    if (!window.confirm(`Eliminare "${filename}"?`)) return;
    try {
      await dashboardProDeleteKnowledge(slug, token, docId);
      setKnowledge((prev) => prev.filter((d) => d.doc_id !== docId));
      toast.success("Documento eliminato");
    } catch { toast.error("Errore nell'eliminazione"); }
  };

  const totalChars = knowledge.reduce((sum, d) => sum + (d.char_count || 0), 0);

  return (
    <div className="space-y-5 max-w-2xl">

      {/* ── VOCE ── */}
      <div className="bg-white border border-brand-line rounded-xl p-5">
        <h2 className="font-heading text-lg text-brand-ink mb-1 flex items-center gap-2">
          <Settings className="w-4 h-4 text-brand-inkSoft" /> Voce predefinita
        </h2>
        <p className="text-sm text-brand-inkSoft mb-4">
          La voce che l'AI usa per rispondere alle chiamate. I visitatori del demo possono comunque cambiarla.
        </p>
        {loadingSettings ? (
          <Loader2 className="w-5 h-5 animate-spin text-brand-inkSoft" />
        ) : (
          <>
            <div className="flex gap-3 mb-4">
              {[["M", "Voce maschile", "Roberto"], ["F", "Voce femminile", "Anna"]].map(([val, label, name]) => (
                <button
                  key={val}
                  onClick={() => setVoice(val)}
                  className={`flex-1 py-3 px-4 rounded-xl border-2 text-left transition ${
                    voice === val
                      ? "border-brand-olive bg-brand-olive/5"
                      : "border-brand-line hover:border-brand-inkSoft"
                  }`}
                >
                  <p className={`font-semibold text-sm ${voice === val ? "text-brand-olive" : "text-brand-ink"}`}>
                    {label}
                  </p>
                  <p className="text-xs text-brand-inkSoft">{name}</p>
                </button>
              ))}
            </div>
            <button
              onClick={saveVoice}
              disabled={savingVoice}
              className="btn-olive px-5 py-2 rounded-lg text-sm font-semibold disabled:opacity-50 flex items-center gap-2"
            >
              {savingVoice ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              Salva voce
            </button>
          </>
        )}
      </div>

      {/* ── KNOWLEDGE BASE ── */}
      <div className="bg-white border border-brand-line rounded-xl p-5">
        <div className="flex items-start justify-between mb-1">
          <h2 className="font-heading text-lg text-brand-ink flex items-center gap-2">
            <FileText className="w-4 h-4 text-brand-inkSoft" /> Base di conoscenza
          </h2>
          {knowledge.length > 0 && (
            <span className="text-xs text-brand-inkSoft bg-brand-bg px-2 py-1 rounded-lg">
              {totalChars.toLocaleString()} / 50.000 car.
            </span>
          )}
        </div>
        <p className="text-sm text-brand-inkSoft mb-4">
          Carica PDF o file di testo con informazioni sull'azienda (listino prezzi, FAQ, menù, orari stagionali…).
          L'AI li legge e risponde di conseguenza durante le chiamate.
        </p>

        {/* Upload area */}
        <div
          onClick={() => !uploading && fileRef.current?.click()}
          className={`border-2 border-dashed rounded-xl p-6 text-center transition mb-4 ${
            uploading
              ? "border-brand-olive bg-brand-olive/5 cursor-wait"
              : "border-brand-line hover:border-brand-olive hover:bg-brand-olive/5 cursor-pointer"
          }`}
        >
          {uploading ? (
            <div className="flex flex-col items-center gap-2">
              <Loader2 className="w-7 h-7 text-brand-olive animate-spin" />
              <p className="text-sm text-brand-inkSoft">Lettura e caricamento in corso…</p>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2">
              <Upload className="w-7 h-7 text-brand-inkSoft" />
              <p className="text-sm text-brand-ink font-medium">Clicca per caricare un documento</p>
              <p className="text-xs text-brand-inkSoft">PDF, TXT, MD · max 30.000 caratteri per file</p>
            </div>
          )}
        </div>
        <input
          ref={fileRef}
          type="file"
          accept=".pdf,.txt,.md,.csv"
          className="hidden"
          onChange={handleFileChange}
        />

        {/* File list */}
        {loadingDocs ? (
          <div className="flex justify-center py-3">
            <Loader2 className="w-5 h-5 animate-spin text-brand-inkSoft" />
          </div>
        ) : knowledge.length > 0 ? (
          <div className="space-y-2">
            {knowledge.map((doc) => (
              <div key={doc.doc_id} className="flex items-center gap-3 bg-brand-bg rounded-lg px-4 py-2.5">
                <FileText className="w-4 h-4 text-brand-olive shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-brand-ink font-medium truncate">{doc.filename}</p>
                  <p className="text-xs text-brand-inkSoft">
                    {(doc.char_count || 0).toLocaleString()} caratteri · {fmtDate(doc.uploaded_at)}
                  </p>
                </div>
                <button
                  onClick={() => deleteDoc(doc.doc_id, doc.filename)}
                  className="text-brand-inkSoft hover:text-red-500 transition p-1 shrink-0"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-brand-inkSoft text-center py-2 italic">
            Nessun documento caricato ancora.
          </p>
        )}
      </div>
    </div>
  );
}

function EmptyState({ icon, title, subtitle }) {
  return (
    <div className="bg-white border border-brand-line rounded-2xl p-12 text-center">
      <div className="flex justify-center mb-4 text-brand-line">{icon}</div>
      <p className="font-heading text-xl text-brand-ink mb-2">{title}</p>
      <p className="text-sm text-brand-inkSoft max-w-xs mx-auto">{subtitle}</p>
    </div>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("it-IT", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}
